import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, Package, ShoppingCart, Users, Tag, Layers, LogOut,
  Bell, Search, Sun, Moon, ChevronDown, ChevronRight, Home, Settings, HelpCircle, MoreHorizontal
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Avatar, AvatarFallback } from './ui/avatar';
import { AdminTour } from './AdminTour';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator
} from './ui/dropdown-menu';
import { useAdminTheme } from '../contexts/AdminThemeContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL ? `${BACKEND_URL.replace(/\/$/, '')}/api` : '/api';
const TOUR_STATE_KEY = 'adminTourState';

export const AdminLayout = ({ children, title, subtitle }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { darkMode, toggleTheme, theme } = useAdminTheme();
  
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [userRole, setUserRole] = useState(localStorage.getItem('userRole') || 'admin');
  const [userName, setUserName] = useState('');
  const [notifications, setNotifications] = useState([]);
  const [globalSearch, setGlobalSearch] = useState('');
  const [tourOpen, setTourOpen] = useState(() => {
    try {
      const raw = localStorage.getItem(TOUR_STATE_KEY);
      const parsed = raw ? JSON.parse(raw) : null;
      return !!parsed?.open;
    } catch {
      return false;
    }
  });
  const [tourIndex, setTourIndex] = useState(() => {
    try {
      const raw = localStorage.getItem(TOUR_STATE_KEY);
      const parsed = raw ? JSON.parse(raw) : null;
      return Number.isFinite(parsed?.index) ? parsed.index : 0;
    } catch {
      return 0;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(TOUR_STATE_KEY, JSON.stringify({ open: tourOpen, index: tourIndex }));
    } catch {
      // ignore
    }
  }, [tourOpen, tourIndex]);

  const token = localStorage.getItem('adminToken');
  const isProgrammer = userRole === 'programmer';
  const canManageCatalog = userRole === 'programmer' || userRole === 'admin';
  const unreadNotifications = notifications.filter(n => !n.read).length;

  const runGlobalSearch = () => {
    const q = globalSearch.trim();
    if (!q) return;

    const params = new URLSearchParams();
    params.set('q', q);

    const isOnOrders = location.pathname.startsWith('/admin/orders');
    const isOnProducts = location.pathname.startsWith('/admin/products');
    const isOnUsers = location.pathname.startsWith('/admin/users');

    if (isOnOrders) {
      navigate(`/admin/orders?${params.toString()}`);
      return;
    }
    if (isOnProducts) {
      navigate(`/admin/products?${params.toString()}`);
      return;
    }
    if (isOnUsers) {
      navigate(`/admin/users?${params.toString()}`);
      return;
    }

    const looksLikeOrder = /^#?[a-f0-9\-]{6,}$/i.test(q);
    if (q.includes('@') || looksLikeOrder) {
      navigate(`/admin/orders?${params.toString()}`);
      return;
    }
    navigate(`/admin/products?${params.toString()}`);
  };

  useEffect(() => {
    fetchCurrentUser();
  }, []);

  useEffect(() => {
    if (!token) return;

    const formatDateTime = (iso) => {
      if (!iso) return 'Agora';
      const date = new Date(iso);
      if (Number.isNaN(date.getTime())) return 'Agora';
      return date.toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    };

    const fetchNotifications = async () => {
      try {
        const response = await axios.get(`${API}/admin/stats`, {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true
        });

        const pending = response.data?.orders_by_status?.pending || 0;
        const latestPendingAt = response.data?.latest_pending_order_created_at;
        const lowStockProducts = response.data?.low_stock_products || [];
        const lowStockThreshold = response.data?.low_stock_threshold;

        const next = [];

        if (pending > 0) {
          next.push({
            id: 'pending_orders',
            title: pending === 1 ? '1 pedido pendente' : `${pending} pedidos pendentes`,
            time: formatDateTime(latestPendingAt),
            read: false,
          });
        }

        if (lowStockProducts.length > 0) {
          const thresholdText = typeof lowStockThreshold === 'number' ? ` (<= ${lowStockThreshold})` : '';
          next.push({
            id: 'low_stock',
            title: lowStockProducts.length === 1
              ? `1 produto com estoque baixo${thresholdText}`
              : `${lowStockProducts.length} produtos com estoque baixo${thresholdText}`,
            time: 'Agora',
            read: false,
          });
        }

        setNotifications(next);
      } catch (error) {
        if (error.response?.status === 401 || error.response?.status === 403) {
          navigate('/admin/login');
        }
      }
    };

    fetchNotifications();
    const interval = setInterval(fetchNotifications, 15000);
    return () => clearInterval(interval);
  }, [navigate, token]);

  const fetchCurrentUser = async () => {
    const currentToken = localStorage.getItem('adminToken');
    if (!currentToken) {
      navigate('/admin/login');
      return;
    }
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${currentToken}` },
        withCredentials: true
      });
      setUserRole(response.data.role);
      setUserName(response.data.name || response.data.email);
      localStorage.setItem('userRole', response.data.role);
    } catch (error) {
      navigate('/admin/login');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('userRole');
    window.location.href = '/';
  };

  const menuItems = [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/admin', show: true },
    { icon: Package, label: 'Produtos', path: '/admin/products', show: canManageCatalog },
    { icon: ShoppingCart, label: 'Pedidos', path: '/admin/orders', show: true },
    { icon: Users, label: 'Clientes', path: '/admin/users', show: true },
    { icon: Tag, label: 'Cupons', path: '/admin/coupons', show: true },
    { icon: Layers, label: 'Categorias', path: '/admin/categories', show: canManageCatalog },
  ];

  const tourSteps = [
    {
      path: '/admin',
      selector: '[data-tour="admin-sidebar"]',
      title: '1. Menu Lateral',
      description: 'Use este menu para navegar entre as seções: Dashboard, Produtos, Pedidos, Clientes, Cupons e Categorias. Vamos conhecer cada uma!'
    },
    {
      path: '/admin',
      selector: '[data-tour="admin-stats"]',
      title: '2. Cards de Estatísticas',
      description: 'Visão rápida do negócio: Faturamento Total, Total de Pedidos, Clientes cadastrados e Produtos no catálogo.'
    },
    {
      path: '/admin',
      selector: '[data-tour="admin-charts"]',
      title: '3. Gráficos',
      description: 'Gráfico de faturamento dos últimos 6 meses, status dos pedidos e produtos mais vendidos.'
    },
    {
      path: '/admin',
      selector: '[data-tour="admin-theme"]',
      title: '4. Tema',
      description: 'Alterne entre modo claro e escuro. A preferência é salva automaticamente.'
    },
    {
      path: '/admin',
      selector: '[data-tour="admin-notifications"]',
      title: '5. Notificações',
      description: 'Alertas de pedidos pendentes e estoque baixo. O número vermelho mostra quantas não lidas.'
    },
    {
      path: '/admin/orders',
      selector: '[data-tour="orders-filters"]',
      title: '🛒 Pedidos - Filtros',
      description: 'Busque pedidos por ID ou email. Use o filtro de status para ver apenas Pendentes, Confirmados, Enviados, etc.'
    },
    {
      path: '/admin/orders',
      selector: '[data-tour="orders-table"]',
      title: '🛒 Pedidos - Tabela',
      description: 'Lista de pedidos com ID, Cliente, Data, Total, Pagamento e Status. Clique no Status para mudar. O Pagamento atualiza automaticamente!'
    },
    {
      path: '/admin/products',
      selector: '[data-tour="products-header"]',
      title: '📦 Produtos - Ações',
      description: 'Busque produtos pelo nome. Clique em "Novo Produto" para adicionar com nome, preço, descrição, imagens, estoque e categoria.'
    },
    {
      path: '/admin/users',
      selector: '[data-tour="users-stats"]',
      title: '👥 Clientes - Estatísticas',
      description: 'Total de usuários dividido por tipo: Programadores, Administradores e Clientes.'
    },
    {
      path: '/admin/users',
      selector: '[data-tour="users-table"]',
      title: '👥 Clientes - Tabela',
      description: 'Lista de usuários com nome, email, cargo e pedidos. Altere o cargo no dropdown. Exclua pelo botão de lixeira.'
    },
    {
      path: '/admin/coupons',
      selector: '[data-tour="coupons-header"]',
      title: '🏷️ Cupons - Criar',
      description: 'Clique em "Novo Cupom" para criar. Defina: código, tipo (% ou R$), valor, desconto máximo, compra mínima, limite de usos e expiração.'
    },
    {
      path: '/admin/categories',
      selector: '[data-tour="categories-header"]',
      title: '📁 Categorias - Gerenciar',
      description: 'Veja o total de categorias. Clique em "Nova Categoria" para criar. Defina nome, slug (URL), descrição e imagem.'
    },
    {
      path: '/admin',
      selector: '[data-tour="admin-user-menu"]',
      title: '✅ Tutorial Concluído!',
      description: 'Você conheceu todas as abas! Os dados atualizam automaticamente. Clique em Ajuda para rever este tutorial a qualquer momento.'
    }
  ];

  return (
    <div className={`min-h-screen ${theme.bg} flex`}>
      {/* Sidebar */}
      <aside data-tour="admin-sidebar" className={`hidden md:flex ${sidebarCollapsed ? 'w-20' : 'w-64'} ${theme.sidebar} border-r transition-all duration-300 flex-col fixed h-full z-20`}>
        {/* Logo */}
        <div className={`h-16 flex items-center ${sidebarCollapsed ? 'justify-center px-2' : 'px-6'} border-b ${darkMode ? 'border-slate-700' : 'border-gray-200'}`}>
          {sidebarCollapsed ? (
            <img 
              src="/logo-pcs.png" 
              alt="PCS Dutos" 
              className={`h-10 w-auto ${darkMode ? '' : 'brightness-0'}`}
            />
          ) : (
            <div className="flex items-center space-x-3">
              <img
                src="/logo-pcs.png"
                alt="PCS Dutos"
                className={`h-12 w-auto ${darkMode ? '' : 'brightness-0'}`}
              />
              <div>
                <span className={`font-bold text-lg ${theme.text}`}>PCS Admin</span>
                <span className={`block text-xs ${theme.textMuted}`}>Painel de Controle</span>
              </div>
            </div>
          )}
        </div>

        {/* Navigation */}
        <nav className="flex-1 py-6 px-3 space-y-1 overflow-y-auto">
          {menuItems.filter(item => item.show).map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center ${sidebarCollapsed ? 'justify-center' : ''} space-x-3 px-4 py-3 rounded-xl transition-all duration-200 ${
                  isActive 
                    ? theme.active + ' border'
                    : `${theme.textMuted} ${theme.hover}`
                }`}
              >
                <item.icon className="h-5 w-5 flex-shrink-0" />
                {!sidebarCollapsed && <span className="font-medium">{item.label}</span>}
              </Link>
            );
          })}
        </nav>

        {/* Sidebar Footer */}
        <div className={`p-4 border-t ${darkMode ? 'border-slate-700' : 'border-gray-200'}`}>
          <button
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className={`w-full flex items-center ${sidebarCollapsed ? 'justify-center' : ''} space-x-3 px-4 py-3 rounded-xl ${theme.textMuted} ${theme.hover} transition-colors`}
          >
            <ChevronRight className={`h-5 w-5 transition-transform ${sidebarCollapsed ? '' : 'rotate-180'}`} />
            {!sidebarCollapsed && <span>Recolher</span>}
          </button>
        </div>
      </aside>

      {/* Mobile Bottom Navigation */}
      <nav className={`md:hidden fixed bottom-0 left-0 right-0 z-30 ${theme.sidebar} border-t ${darkMode ? 'border-slate-700' : 'border-gray-200'}`}>
        <div className="grid grid-cols-5 gap-1 px-2 py-2">
          {(() => {
            const visible = menuItems.filter(item => item.show);
            const primary = visible.slice(0, 4);
            const more = visible.slice(4);
            const isMoreActive = more.some(item => location.pathname === item.path);

            return (
              <>
                {primary.map((item) => {
                  const isActive = location.pathname === item.path;
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      className={`flex flex-col items-center justify-center rounded-xl px-2 py-2 text-xs transition-colors ${
                        isActive ? theme.active + ' border' : `${theme.textMuted} ${theme.hover}`
                      }`}
                    >
                      <item.icon className="h-5 w-5 mb-1" />
                      <span className="leading-none">{item.label}</span>
                    </Link>
                  );
                })}

                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      type="button"
                      className={`flex flex-col items-center justify-center rounded-xl px-2 py-2 text-xs transition-colors ${
                        isMoreActive ? theme.active + ' border' : `${theme.textMuted} ${theme.hover}`
                      }`}
                    >
                      <MoreHorizontal className="h-5 w-5 mb-1" />
                      <span className="leading-none">Mais</span>
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className={`w-56 ${theme.dropdown}`}>
                    {more.map((item) => (
                      <DropdownMenuItem key={item.path} className={theme.text} onClick={() => navigate(item.path)}>
                        <item.icon className="h-4 w-4 mr-2" />
                        {item.label}
                      </DropdownMenuItem>
                    ))}

                    <DropdownMenuSeparator className={darkMode ? 'bg-slate-700' : 'bg-gray-200'} />
                    <DropdownMenuItem className={theme.text} onClick={() => navigate('/admin/settings')}>
                      <Settings className="h-4 w-4 mr-2" />
                      Configurações
                    </DropdownMenuItem>
                    <DropdownMenuItem className={theme.text} onClick={() => navigate('/admin/notifications')}>
                      <Bell className="h-4 w-4 mr-2" />
                      Notificações
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            );
          })()}
        </div>
      </nav>

      {/* Main Content */}
      <main className={`flex-1 ml-0 ${sidebarCollapsed ? 'md:ml-20' : 'md:ml-64'} transition-all duration-300 pb-24 md:pb-0`}>
        {/* Header */}
        <header className={`${theme.header} backdrop-blur-xl border-b px-4 md:px-6 py-3 md:py-4 sticky top-0 z-10`}>
          <div className="flex items-center justify-between">
            {/* Left side */}
            <div className="flex items-center space-x-4">
              <div>
                <h1 className={`text-2xl font-bold ${theme.text}`}>{title}</h1>
                {subtitle && <p className={`text-sm ${theme.textMuted}`}>{subtitle}</p>}
              </div>
            </div>

            {/* Right side */}
            <div className="flex items-center space-x-3">
              {/* Search */}
              <div className="hidden md:flex items-center">
                <div className="relative">
                  <Search className={`absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 ${theme.textMuted}`} />
                  <Input 
                    placeholder="Buscar..." 
                    value={globalSearch}
                    onChange={(e) => setGlobalSearch(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        runGlobalSearch();
                      }
                    }}
                    className={`pl-10 w-64 ${theme.input} rounded-xl border focus:ring-2 focus:ring-blue-500`}
                  />
                </div>
              </div>

              {/* Theme Toggle */}
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleTheme}
                className={`rounded-xl ${theme.hover}`}
                data-tour="admin-theme"
              >
                {darkMode ? <Sun className="h-5 w-5 text-yellow-500" /> : <Moon className="h-5 w-5 text-slate-600" />}
              </Button>

              {/* Notifications */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className={`rounded-xl ${theme.hover} relative`} data-tour="admin-notifications">
                    <Bell className={`h-5 w-5 ${theme.textMuted}`} />
                    {unreadNotifications > 0 && (
                      <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                        {unreadNotifications}
                      </span>
                    )}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className={`w-80 ${theme.dropdown}`}>
                  <div className={`px-4 py-3 border-b ${darkMode ? 'border-slate-700' : 'border-gray-200'}`}>
                    <p className={`font-semibold ${theme.text}`}>Notificações</p>
                    <p className={`text-sm ${theme.textMuted}`}>{unreadNotifications} não lidas</p>
                  </div>
                  {notifications.map((notif) => (
                    <DropdownMenuItem key={notif.id} className={`px-4 py-3 ${!notif.read ? (darkMode ? 'bg-slate-700/50' : 'bg-blue-50') : ''}`}>
                      <div className="flex items-start space-x-3">
                        <div className={`w-2 h-2 rounded-full mt-2 ${notif.read ? 'bg-gray-300' : 'bg-blue-500'}`}></div>
                        <div>
                          <p className={`text-sm font-medium ${theme.text}`}>{notif.title}</p>
                          <p className={`text-xs ${theme.textMuted}`}>{notif.time}</p>
                        </div>
                      </div>
                    </DropdownMenuItem>
                  ))}
                  <DropdownMenuSeparator className={darkMode ? 'bg-slate-700' : 'bg-gray-200'} />
                  <DropdownMenuItem
                    className="justify-center text-blue-500 font-medium"
                    onClick={() => navigate('/admin/notifications')}
                  >
                    Ver todas
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              {/* User Menu */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className={`flex items-center space-x-3 px-3 py-2 rounded-xl ${theme.hover}`} data-tour="admin-user-menu">
                    <Avatar className="h-9 w-9">
                      <AvatarFallback className="bg-gradient-to-br from-blue-500 to-purple-600 text-white">
                        {userName?.charAt(0)?.toUpperCase() || 'A'}
                      </AvatarFallback>
                    </Avatar>
                    <div className="hidden md:block text-left">
                      <p className={`text-sm font-medium ${theme.text}`}>{userName || 'Admin'}</p>
                      <p className={`text-xs ${theme.textMuted}`}>{userRole === 'programmer' ? 'Programador' : 'Administrador'}</p>
                    </div>
                    <ChevronDown className={`h-4 w-4 ${theme.textMuted}`} />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className={`w-56 ${theme.dropdown}`}>
                  <DropdownMenuItem onClick={() => navigate('/')} className={theme.text}>
                    <Home className="h-4 w-4 mr-2" />
                    Ver Site
                  </DropdownMenuItem>
                  <DropdownMenuItem className={theme.text} onClick={() => navigate('/admin/settings')}>
                    <Settings className="h-4 w-4 mr-2" />
                    Configurações
                  </DropdownMenuItem>
                  <DropdownMenuItem className={theme.text} onClick={() => { setTourIndex(0); setTourOpen(true); }}>
                    <HelpCircle className="h-4 w-4 mr-2" />
                    Ajuda
                  </DropdownMenuItem>
                  <DropdownMenuSeparator className={darkMode ? 'bg-slate-700' : 'bg-gray-200'} />
                  <DropdownMenuItem onClick={handleLogout} className="text-red-500">
                    <LogOut className="h-4 w-4 mr-2" />
                    Sair
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>

          {/* Mobile Search */}
          <div className="md:hidden mt-3">
            <div className="relative">
              <Search className={`absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 ${theme.textMuted}`} />
              <Input
                placeholder="Buscar..."
                value={globalSearch}
                onChange={(e) => setGlobalSearch(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    runGlobalSearch();
                  }
                }}
                className={`pl-10 w-full ${theme.input} rounded-xl border focus:ring-2 focus:ring-blue-500`}
              />
            </div>
          </div>
        </header>

        {/* Page Content */}
        <div className="p-6">
          {children}
        </div>
      </main>

      <AdminTour
        open={tourOpen}
        onClose={() => setTourOpen(false)}
        steps={tourSteps}
        storageKey="adminTourCompleted"
        initialIndex={tourIndex}
        onIndexChange={setTourIndex}
      />
    </div>
  );
};
